import { isSome } from '@crocodille/util';
import useSWR, { unstable_serialize, useSWRConfig } from 'swr';
import useSWRMutation, { SWRMutationConfiguration } from 'swr/mutation';

import { App } from '../app';
import { useApp } from '../app-provider';

import type { RouterInputs } from '@crocodille/api';

/**
 * Refresh interval for the customer data.
 * We want to refresh it often when the user is logged in,
 * but not when they are logged out.
 */
function refreshInterval(data: Customer | undefined) {
  if (isSome(data)) {
    return 60 * 1000; // 1 minute
  } else {
    return 0; // never
  }
}

export async function fetchAuthorizedCustomer(app: Pick<App, 'trpc'>) {
  const customer = await app.trpc.customer.me.query();

  return customer;
}

export type Customer = Awaited<ReturnType<typeof fetchAuthorizedCustomer>>;

export const AUTHORIZED_CUSTOMER_KEY = 'authorizedCustomer' as const;

export function useAuthorizedCustomer() {
  const app = useApp();

  const customerResponse = useSWR(
    AUTHORIZED_CUSTOMER_KEY,
    async () => {
      const customer = await fetchAuthorizedCustomer(app);

      return customer;
    },
    {
      refreshInterval,
      revalidateOnMount: true,
      revalidateOnFocus: true,
      onSuccess: (customer) => {
        if (customer === null) {
          app.onUserUnauthorized?.();
        }
      },
    },
  );

  return customerResponse;
}

export function useAuthorizedCustomerAction<A = void>(
  action: (arg: A) => Promise<Customer | undefined>,
  options?: SWRMutationConfiguration<
    Customer | undefined,
    Error,
    A,
    typeof AUTHORIZED_CUSTOMER_KEY
  >,
) {
  const { trpc } = useApp();
  const { mutate } = useSWRConfig();

  return useSWRMutation(
    AUTHORIZED_CUSTOMER_KEY,
    async (_, { arg }: { arg: A }) => {
      let customer = await action(arg);

      if (customer === undefined) {
        customer = await fetchAuthorizedCustomer({ trpc });
      }

      return customer;
    },
    {
      populateCache: true,
      revalidate: false,
      ...options,
      onSuccess: (data, key, config) => {
        options?.onSuccess?.(data, key, config);

        //
        // Logout
        //
        if (data === null) {
          // clear all cache
          mutate(
            () => true, // which cache keys are updated
            undefined, // update cache data to `undefined`
            { revalidate: false }, // do not revalidate
          );
        }
      },
    },
  );
}

export function asAuthorizedCustomerFallback(data: Customer) {
  return {
    [unstable_serialize(AUTHORIZED_CUSTOMER_KEY)]: data,
  };
}

export async function fetchAuthorizedCustomerFallback(app: App) {
  return asAuthorizedCustomerFallback(await fetchAuthorizedCustomer(app));
}

export async function authorizeUsingCredentials(
  app: App,
  input: RouterInputs['auth']['credentials'],
) {
  const response = await app.trpc.auth.credentials.mutate(input);

  return response;
}

export async function authorizeUsingToken(
  app: App,
  input: RouterInputs['auth']['token'],
) {
  const response = await app.trpc.auth.token.mutate(input);

  return response;
}
